import { useRefreshTick } from "../lib/useAutoRefresh";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { money } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { useRoles } from "../lib/useRoles";
import { Users, PlayCircle, History, FileText, ArrowRight, Building2 } from "lucide-react";
import NextRemittance from "../components/NextRemittance";
import { ClientDepositCard } from "../components/ClientDepositCard";

export default function ClientDashboard() {
  const { user } = useAuth();
  const [company, setCompany] = useState(null);
  const [emps, setEmps] = useState([]);
  const [runs, setRuns] = useState([]);
  const [canViewRemit, setCanViewRemit] = useState(true);
  const tick = useRefreshTick();
  const role = useRoles().base("client", user?.client_role || "admin");

  useEffect(() => {
    api.get("/companies").then((r) => {
      const co = r.data[0];
      setCompany(co);
      if (co) {
        api.get(`/employees?company_id=${co.company_id}`).then((e) => setEmps(e.data)).catch(() => {});
        api.get(`/payroll/runs?company_id=${co.company_id}`).then((h) => setRuns(h.data)).catch(() => {});
      }
    }).catch(() => {});
  }, [tick]);

  useEffect(() => {
    api.get("/settings/client-permissions").then((r) => {
      const perms = r.data?.permissions;
      setCanViewRemit(perms ? (perms[role]?.remittance?.view ?? true) : true);
    }).catch(() => {});
  }, [role]);

  const lastRun = runs[0];
  const stats = [
    { label: "Employees", value: emps.length, icon: Users },
    { label: "Payroll runs", value: runs.length, icon: History },
    { label: "Last net pay", value: lastRun ? money(lastRun.totals?.net_pay) : "—", icon: PlayCircle },
  ];
  const quick = [
    { to: "/client/run-payroll", label: "Run payroll", caps: ["admin", "preparer"], icon: PlayCircle },
    { to: "/client/company", label: "Company profile", caps: ["admin"], icon: Building2 },
    { to: "/client/history", label: "View pay history", icon: History },
    { to: "/client/t4", label: "T4 slips", icon: FileText },
  ].filter((q) => !q.caps || q.caps.includes(role));

  return (
    <div data-testid="client-dashboard">
      <h1 className="text-3xl font-bold tracking-tight text-slate-900">Welcome back, {user?.name?.split(" ")[0]}</h1>
      <p className="text-slate-500 mt-1">{company?.name || "Your company"} · payroll at a glance</p>

      {company && canViewRemit && (
        <div className="mt-6">
          <NextRemittance companyId={company.company_id} remittanceHref="/client/remittance" variant="banner" />
        </div>
      )}

      <div className="grid sm:grid-cols-3 gap-4 mt-8">
        {stats.map((s) => (
          <div key={s.label} data-testid={`stat-${s.label}`} className="rounded-2xl bg-white border border-slate-200 p-5 shadow-sm">
            <div className="flex items-center gap-2 text-slate-500 text-sm"><s.icon className="h-4 w-4" /> {s.label}</div>
            <div className="mt-2 text-3xl font-bold text-slate-900 tabular-nums">{s.value}</div>
          </div>
        ))}
      </div>

      {company && <ClientDepositCard company={company} role={role} />}

      <h2 className="mt-10 text-lg font-semibold text-slate-900">Quick actions</h2>
      <div className="grid sm:grid-cols-2 gap-3 mt-3">
        {quick.map((q) => (
          <Link key={q.to} to={q.to} data-testid={`quick-${q.label}`} className="group flex items-center justify-between rounded-2xl bg-white border border-slate-200 p-5 hover:border-sky-400 hover:shadow-md transition-all">
            <span className="flex items-center gap-3 font-medium text-slate-800"><q.icon className="h-5 w-5 text-sky-600" /> {q.label}</span>
            <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-sky-600 group-hover:translate-x-1 transition-all" />
          </Link>
        ))}
      </div>
    </div>
  );
}
